import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Droplets, 
  Sun, 
  Map, 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// ICONS
// ============================================

const DroneIcon = ({ size = 24, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="5" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    <circle cx="5" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
    <line x1="5" y1="7" x2="5" y2="17" />
    <line x1="19" y1="7" x2="19" y2="17" />
    <line x1="7" y1="5" x2="17" y2="5" />
    <line x1="7" y1="19" x2="17" y2="19" />
    <rect x="9" y="9" width="6" height="6" rx="1" />
  </svg>
);

// ============================================
// CONSTANTS
// ============================================

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pesticide-drone-app/artifacts/ab12mzl7_colored-logo-removebg-preview.png";

const NAV_LINKS = [
  { name: "About", href: "#about" },
  { name: "Services", href: "#services" },
  { name: "Service Area", href: "#service-area" },
  { name: "Contact", href: "#quote" },
];

const SERVICES_LIST = [
  { title: "Agricultural Drone Spraying", icon: DroneIcon },
  { title: "Pesticide Application", icon: Droplets },
  { title: "Mosquito Spraying", icon: Sun },
  { title: "Right of Way Spraying", icon: Map },
];

const SERVICE_OPTIONS = [
  "Agricultural Drone Spraying",
  "Pesticide Application",
  "Mosquito Spraying",
  "Right of Way Spraying",
  "Other"
];

const INITIAL_FORM_STATE = {
  name: "",
  farm_name: "",
  phone: "",
  email: "",
  service_needed: "",
  acreage: "",
  location: "",
  message: ""
};

// ============================================
// REUSABLE COMPONENTS
// ============================================

const Logo = ({ className = "h-14 w-auto" }) => (
  <img 
    src={LOGO_URL}
    alt="Donaldson Precision Spraying" 
    className={className}
    data-testid="company-logo"
  />
);

const SectionHeader = ({ label, title, light = false }) => (
  <div className="text-center mb-12">
    <p className={`font-heading text-sm uppercase tracking-widest mb-4 ${light ? 'text-accent' : 'text-primary'}`}>
      {label}
    </p>
    <h2 className={`font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight ${light ? 'text-white' : 'text-foreground'}`}>
      {title}
    </h2>
  </div>
);

// ============================================
// NAVIGATION
// ============================================

const DesktopNav = () => (
  <div className="hidden md:flex items-center space-x-8">
    {NAV_LINKS.map((link) => (
      <a
        key={link.name}
        href={link.href}
        className="font-heading text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
        data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
      >
        {link.name}
      </a>
    ))}
    <a
      href="#quote"
      className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 text-sm font-heading uppercase tracking-wider rounded-md flex items-center transition-all shadow-md hover:shadow-lg active:scale-95"
      data-testid="nav-get-quote-btn"
    >
      Get a Quote
    </a>
  </div>
);

const MobileNav = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
      {NAV_LINKS.map((link) => (
        <a
          key={link.name}
          href={link.href}
          onClick={onClose}
          className="block py-3 font-heading text-sm uppercase tracking-wider text-foreground hover:text-primary transition-colors"
        >
          {link.name}
        </a>
      ))}
      <a
        href="#quote"
        onClick={onClose}
        className="mt-4 w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 text-sm font-heading uppercase tracking-wider rounded-md flex items-center justify-center transition-all"
      >
        Get a Quote
      </a>
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClose = () => setIsOpen(false);

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="flex items-center" data-testid="logo-link">
            <Logo />
          </a>
          <DesktopNav />
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-foreground hover:bg-muted"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        <MobileNav isOpen={isOpen} onClose={handleClose} />
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION
// ============================================

const Hero = () => (
  <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url('https://images.unsplash.com/photo-1596725217685-1315a39b80d4?auto=format&fit=crop&w=2000&q=80')` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-black/10"></div>
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
      <div className="w-full">
        <div className="mb-8 animate-fade-in flex justify-center" style={{ animationDelay: '0.1s' }}>
          <img 
            src={LOGO_URL}
            alt="Donaldson Precision Spraying" 
            className="w-full max-w-4xl h-auto drop-shadow-[0_0_30px_rgba(0,0,0,0.8)]"
          />
        </div>
        <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto text-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
          We provide professional pesticide application when you need it.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in justify-center" style={{ animationDelay: '0.3s' }}>
          <a
            href="#quote"
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 text-lg font-heading uppercase tracking-wide rounded-md flex items-center justify-center transition-all shadow-lg hover:shadow-xl active:scale-95"
            data-testid="hero-get-quote-btn"
          >
            Get a Free Quote
            <ChevronRight className="ml-2" size={20} />
          </a>
          <a
            href="#services"
            className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 hover:bg-white/20 h-14 px-8 text-lg font-heading uppercase tracking-wide rounded-md flex items-center justify-center transition-all"
            data-testid="hero-services-btn"
          >
            Our Services
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// ABOUT SECTION
// ============================================

const About = () => (
  <section id="about" className="py-20 lg:py-32 bg-white" data-testid="about-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <SectionHeader label="Who We Are" title="Locally Owned Operation from Manvel, ND" />
      <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
        We are a custom spraying service that utilizes DJI Agras T-100 spray drones to provide fast, reliable pesticide applications for farmers, government entities, and anyone who needs it.
      </p>
    </div>
  </section>
);

// ============================================
// SERVICES SECTION
// ============================================

const ServiceCard = ({ service, index }) => (
  <div 
    className="bg-white p-6 rounded-xl border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group text-center"
    data-testid={`service-card-${index}`}
  >
    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 mx-auto group-hover:bg-primary group-hover:text-white transition-colors">
      <service.icon className="text-primary group-hover:text-white transition-colors" size={28} />
    </div>
    <h3 className="font-heading text-base font-semibold text-foreground uppercase">
      {service.title}
    </h3>
  </div>
);

const Services = () => (
  <section id="services" className="py-20 lg:py-32 bg-secondary/30" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader label="What We Offer" title="Our Services" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        {SERVICES_LIST.map((service, index) => (
          <ServiceCard key={service.title} service={service} index={index} />
        ))}
      </div>
    </div>
  </section>
);

// ============================================
// SERVICE AREA SECTION
// ============================================

const ServiceArea = () => (
  <section id="service-area" className="py-20 lg:py-32 relative" data-testid="service-area-section">
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url('https://images.unsplash.com/photo-1551810080-3eb3be72d3f4?auto=format&fit=crop&w=2000&q=80')` }}
    >
      <div className="absolute inset-0 bg-primary/90"></div>
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <SectionHeader label="Where We Operate" title="Serving the Red River Valley and Beyond" light />
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <p className="text-accent font-heading text-lg uppercase tracking-wide mb-2">North Dakota</p>
          <p className="text-lg md:text-xl text-white/90">
            Grand Forks, Walsh, Nelson, Traill, and Steele Counties
          </p>
        </div>
        <div>
          <p className="text-accent font-heading text-lg uppercase tracking-wide mb-2">Minnesota</p>
          <p className="text-lg md:text-xl text-white/90">
            Marshall, Polk, Kittson, Roseau, Pennington, and Red Lake Counties
          </p>
        </div>
      </div>
      <p className="text-lg md:text-xl text-white/90 mt-8">
        Licensed and Insured in Minnesota and North Dakota
      </p>
    </div>
  </section>
);

// ============================================
// VIDEO SECTION
// ============================================

const VideoSection = () => (
  <section className="py-20 lg:py-32 bg-white" data-testid="video-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader label="See Us In Action" title="Watch Our Drones Work" />
      <div className="rounded-2xl overflow-hidden shadow-2xl">
        <video controls className="w-full h-auto">
          <source src="https://customer-assets.emergentagent.com/job_pesticide-drone-app/artifacts/btbi7iin_IMG_0117.mov" type="video/quicktime" />
          <source src="https://customer-assets.emergentagent.com/job_pesticide-drone-app/artifacts/btbi7iin_IMG_0117.mov" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </section>
);

// ============================================
// QUOTE FORM COMPONENTS
// ============================================

const FormInput = ({ label, name, type = "text", value, onChange, required = false, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-2">
      {label} {required && '*'}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full h-12 px-4 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      placeholder={placeholder}
      data-testid={`input-${name.replace('_', '-')}`}
    />
  </div>
);

const FormSelect = ({ label, name, value, onChange, options, required = false }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-2">
      {label} {required && '*'}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full h-12 px-4 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
      data-testid={`select-${name.replace('_', '-')}`}
    >
      <option value="">Select a service...</option>
      {options.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  </div>
);

const FormTextarea = ({ label, name, value, onChange, placeholder, rows = 4 }) => (
  <div className="md:col-span-2">
    <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      className="w-full px-4 py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
      placeholder={placeholder}
      data-testid={`textarea-${name}`}
    />
  </div>
);

const QuoteSuccessMessage = ({ onReset }) => (
  <section id="quote" className="py-20 lg:py-32 bg-secondary/30" data-testid="quote-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="bg-white p-12 rounded-2xl shadow-xl">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <ChevronRight className="text-primary" size={40} />
        </div>
        <h2 className="font-heading text-3xl font-bold text-foreground uppercase mb-4">Thank You!</h2>
        <p className="text-lg text-muted-foreground mb-6">
          We've received your quote request and will be in touch within 24-48 hours.
        </p>
        <button
          onClick={onReset}
          className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 text-lg font-heading uppercase tracking-wide rounded-md transition-all"
          data-testid="submit-another-btn"
        >
          Submit Another Request
        </button>
      </div>
    </div>
  </section>
);

const useQuoteForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      setFormData(INITIAL_FORM_STATE);
    } catch (err) {
      setError("There was an error submitting your request. Please try again or call us directly.");
      // Log error only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Quote submission error:', err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => setSubmitted(false);

  return { formData, isSubmitting, submitted, error, handleChange, handleSubmit, resetForm };
};

const QuoteForm = () => {
  const { formData, isSubmitting, submitted, error, handleChange, handleSubmit, resetForm } = useQuoteForm();

  if (submitted) {
    return <QuoteSuccessMessage onReset={resetForm} />;
  }

  return (
    <section id="quote" className="py-20 lg:py-32 bg-secondary/30" data-testid="quote-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader label="Get Started" title="Request a Free Quote" />
        <p className="text-lg text-muted-foreground text-center -mt-8 mb-12">
          Fill out the form below and we'll get back to you within 24-48 hours.
        </p>

        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-xl" data-testid="quote-form">
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-lg" data-testid="form-error">
              {error}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <FormInput label="Your Name" name="name" value={formData.name} onChange={handleChange} required placeholder="John Smith" />
            <FormInput label="Farm/Business Name" name="farm_name" value={formData.farm_name} onChange={handleChange} placeholder="Smith Family Farms" />
            <FormInput label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="(555) 123-4567" />
            <FormInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" />
            <FormSelect label="Service Needed" name="service_needed" value={formData.service_needed} onChange={handleChange} options={SERVICE_OPTIONS} required />
            <FormInput label="Approximate Acreage" name="acreage" value={formData.acreage} onChange={handleChange} required placeholder="e.g., 150 acres" />
            <div className="md:col-span-2">
              <FormInput label="Location/County" name="location" value={formData.location} onChange={handleChange} required placeholder="County, State" />
            </div>
            <FormTextarea label="Additional Details" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us more about your needs, crop type, timing, etc." />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-8 w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 text-lg font-heading uppercase tracking-wide rounded-md transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            data-testid="submit-quote-btn"
          >
            {isSubmitting ? <span>Submitting...</span> : (
              <>
                Request Free Quote
                <ChevronRight className="ml-2" size={20} />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

// ============================================
// FOOTER COMPONENTS
// ============================================

const FooterBranding = () => (
  <div>
    <div className="bg-white p-2 rounded-lg inline-block mb-4">
      <Logo />
    </div>
    <p className="text-white/80 leading-relaxed">
      We provide precise agricultural drone spraying services for crops, CRP, and land management needs.
    </p>
  </div>
);

const FooterQuickLinks = () => (
  <div>
    <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Quick Links</h3>
    <ul className="space-y-3">
      {["About", "Services", "Service Area", "Get a Quote"].map((link) => (
        <li key={link}>
          <a 
            href={`#${link.toLowerCase().replace(/ /g, '-').replace('get-a-quote', 'quote')}`}
            className="text-white/80 hover:text-white transition-colors"
          >
            {link}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const FooterContact = () => (
  <div>
    <h3 className="font-heading text-lg font-bold uppercase tracking-wide mb-4">Contact Us</h3>
    <ul className="space-y-3">
      <li className="flex items-center gap-3">
        <Phone size={18} className="text-accent" />
        <div className="text-white/80">
          <span className="block">Ethan: 701-410-8049</span>
          <span className="block">Alex: 218-988-2886</span>
        </div>
      </li>
      <li className="flex items-center gap-3">
        <Mail size={18} className="text-accent" />
        <a href="mailto:donaldsonprecisionspraying@gmail.com" className="text-white/80 hover:text-white transition-colors">
          donaldsonprecisionspraying@gmail.com
        </a>
      </li>
      <li className="flex items-center gap-3">
        <MapPin size={18} className="text-accent" />
        <span className="text-white/80">Red River Valley & Beyond</span>
      </li>
    </ul>
  </div>
);

const Footer = () => (
  <footer className="bg-primary text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-12">
        <FooterBranding />
        <FooterQuickLinks />
        <FooterContact />
      </div>
      <div className="border-t border-white/20 mt-12 pt-8 text-center">
        <p className="text-white/60 text-sm">
          © {new Date().getFullYear()} Donaldson Precision Spraying. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// ============================================
// MAIN APP
// ============================================

function App() {
  return (
    <div className="App" data-testid="app-container">
      <Navbar />
      <main>
        <Hero />
        <About />
        <Services />
        <ServiceArea />
        <VideoSection />
        <QuoteForm />
      </main>
      <Footer />
    </div>
  );
}

export default App;
